import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useWhoAmI } from '../../auth/AdminGate';

type InternalReport = {
  to: string;
  title: string;
  description: string;
  /** Restrict the card to `god`-role users. The endpoint enforces this
   *  server-side too (403 for non-god); this just hides the link. */
  godOnly?: boolean;
};

type ExternalLink = {
  href: string;
  title: string;
  description: string;
};

const REPORTS: InternalReport[] = [
  {
    to: '/reports/inventory',
    title: 'Inventory',
    description:
      'Cost and retail value of on-hand stock, by tenant. Pre-tax.',
  },
  {
    to: '/reports/income',
    title: 'Income',
    description:
      'Yearly and monthly sales totals across all tenants. Subtotal pre-tax; total post-tax.',
  },
  {
    to: '/reports/daily-sales',
    title: 'Daily sales & weather',
    description:
      'Bridgewater in-store sales per day plotted against that day’s weather. Rolling window from a month to a year.',
    godOnly: true,
  },
  {
    to: '/reports/category-sales',
    title: 'Sales by category',
    description:
      'What sells best — units sold and gross revenue bucketed by category over a selectable window (week to year). In-store plus web orders.',
  },
  {
    to: '/reports/sku-sales',
    title: 'Sales by SKU',
    description:
      'Every product sold in a calendar year, sectioned by shop, with a CSV download. Gross in-store sales before tax. Built for sharing outside the business.',
    godOnly: true,
  },
  {
    to: '/reports/sobeys-prices',
    title: 'Competitor price check',
    description:
      'What Sobeys and Superstore in Bridgewater charge for each front-room item, regular and sale, against our cost. Flags anything they sell for less than we pay; review unmatched items here.',
  },
  {
    // Temporary cleanup report; remove the link + page once the orphan
    // list converges to zero. Backend + route stay until then.
    to: '/reports/items-without-category',
    title: 'Items without category (cleanup)',
    description:
      'Non-deleted items whose categoryId is missing or empty. Click a row to assign one.',
  },
];

// External back-office portals. Hosts that aren't part of this app —
// open in a new tab so the user keeps their place here.
const EXTERNAL_LINKS: ExternalLink[] = [
  {
    href: 'https://login.merrco.ca',
    title: 'Online back office',
    description: 'Merrco portal — card payments from the website (Paysafe).',
  },
  {
    href: 'https://merrco.dashboard.payfacto.com',
    title: 'Brick and mortar back office',
    description: 'Payfacto / Merrco dashboard — in-store card payments.',
  },
];

const ReportsPage = () => {
  const me = useWhoAmI();
  const isGod = me.roles.includes('god');
  const reports = REPORTS.filter((r) => !r.godOnly || isGod);
  return (
  <Box>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
      Reports
    </Typography>
    <Stack spacing={2}>
      {reports.map((r) => (
        <Card key={r.to} variant="outlined">
          <CardActionArea component={RouterLink} to={r.to}>
            <CardContent>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: '#1f2937' }}
              >
                {r.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                {r.description}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Stack>

    <Typography
      variant="overline"
      sx={{ color: '#94a3b8', mt: 4, mb: 1.5, display: 'block', letterSpacing: '0.1em' }}
    >
      External
    </Typography>
    <Stack spacing={2}>
      {EXTERNAL_LINKS.map((l) => (
        <Card key={l.href} variant="outlined">
          <CardActionArea
            component="a"
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: '#1f2937' }}
                >
                  {l.title}
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
              </Box>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                {l.description}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Stack>
  </Box>
  );
};

export default ReportsPage;
